'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RothIraCalculator() {
  const t = useTranslations("tools.rothIraCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [inputs, setInputs] = useState({
    currentBalance: 20000,
    annualContribution: 7000,
    maximize: false,
    returnRate: 6,
    currentAge: 30,
    retirementAge: 65,
    taxRate: 25
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
      {},
      { maxWidth: '900px' },
      { maxWidth: '1000px' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const
    },
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorContent: getResponsiveStyle(
      { display: 'grid', gap: '2rem' },
      { gridTemplateColumns: '1fr', padding: '1.5rem' },
      { gridTemplateColumns: '1fr', padding: '2rem' },
      { gridTemplateColumns: '1fr 1fr', padding: '2rem' }
    ),
    calculatorForm: getResponsiveStyle(
      { background: '#f8fafc', borderRadius: '0.75rem', border: '2px solid #e2e8f0' },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #cbd5e1',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #cbd5e1',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        background: 'white'
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    inputHint: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginTop: '0.25rem',
      display: 'block'
    },
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginTop: '0.5rem'
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontWeight: 500,
      cursor: 'pointer'
    },
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        marginTop: '1rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      { background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: '0.75rem', border: '2px solid #fbbf24' },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { margin: '0 0 1.5rem 0', color: '#92400e', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    resultComparison: {
      background: 'white',
      borderRadius: '0.5rem',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      overflowX: 'auto' as const
    },
    comparisonTable: {
      width: '100%',
      borderCollapse: 'collapse' as const
    },
    resultSummary: getResponsiveStyle(
      { background: 'white', borderRadius: '0.5rem', borderLeft: '4px solid #10b981', color: '#065f46', fontWeight: 600, lineHeight: 1.6 },
      { padding: '1rem', fontSize: '0.9rem' },
      { padding: '1.125rem', fontSize: '0.95rem' },
      { padding: '1.25rem', fontSize: '1rem' }
    ),
    chartContainer: {
      marginTop: '2rem',
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.5rem'
    },
    chartTitle: {
      textAlign: 'center' as const,
      fontWeight: 700,
      color: '#1f2937',
      marginBottom: '1rem'
    },
    errorMessage: {
      background: '#fee2e2',
      color: '#991b1b',
      padding: '0.75rem',
      borderRadius: '0.5rem',
      marginTop: '1rem'
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem', marginBottom: '1.25rem' },
      { fontSize: '2rem', marginBottom: '1.5rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.125rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.5rem'
    },
    insightCard: getResponsiveStyle(
      { background: 'rgba(255, 255, 255, 0.8)', borderLeft: '4px solid #2563eb', borderRadius: '0.5rem', marginBottom: '1rem' },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      display: 'block',
      marginBottom: '0.5rem',
      transition: 'color 0.3s ease'
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleMaximizeChange = (maximize: boolean) => {
    const contribution = maximize
      ? (inputs.currentAge >= 50 ? 8000 : 7000)
      : inputs.annualContribution;
    setInputs(prev => ({ ...prev, maximize, annualContribution: contribution }));
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (inputs.retirementAge <= inputs.currentAge) {
      setError(t('errorRetirementAge'));
      return;
    }

    const years = inputs.retirementAge - inputs.currentAge;
    const returnRate = inputs.returnRate / 100;
    const taxRate = inputs.taxRate / 100;

    let rothBalance = inputs.currentBalance;
    let taxableBalance = inputs.currentBalance;
    let totalPrincipal = inputs.currentBalance;
    let rothInterest = 0;
    let taxableInterest = 0;
    let totalTax = 0;

    const rothData = [{ age: inputs.currentAge, balance: inputs.currentBalance }];
    const taxableData = [{ age: inputs.currentAge, balance: inputs.currentBalance }];
    const principalData = [{ age: inputs.currentAge, balance: inputs.currentBalance }];

    for (let i = 1; i <= years; i++) {
      const currentAgeInLoop = inputs.currentAge + i;
      let yearlyContribution = inputs.annualContribution;

      if (inputs.maximize) {
        yearlyContribution = currentAgeInLoop >= 50 ? 8000 : 7000;
      }

      const rothInterestEarned = rothBalance * returnRate;
      rothBalance = rothBalance + rothInterestEarned + yearlyContribution;
      rothInterest += rothInterestEarned;

      const taxableInterestEarned = taxableBalance * returnRate;
      const taxOnInterest = taxableInterestEarned * taxRate;
      taxableBalance = taxableBalance + taxableInterestEarned - taxOnInterest + yearlyContribution;
      taxableInterest += taxableInterestEarned;
      totalTax += taxOnInterest;

      totalPrincipal += yearlyContribution;

      rothData.push({ age: currentAgeInLoop, balance: rothBalance });
      taxableData.push({ age: currentAgeInLoop, balance: taxableBalance });
      principalData.push({ age: currentAgeInLoop, balance: totalPrincipal });
    }

    const difference = rothBalance - taxableBalance;

    setResults({
      rothBalance,
      taxableBalance,
      totalPrincipal,
      rothInterest,
      taxableInterest,
      totalTax,
      difference,
      rothData,
      taxableData,
      principalData
    });
  };

  const formatCurrency = (value: number) => {
    return '$' + Math.round(value).toLocaleString();
  };

  const drawChart = () => {
    if (!results) return null;

    const { rothData, taxableData, principalData } = results;
    const width = screenSize === 'mobile' ? 300 : screenSize === 'tablet' ? 500 : 700;
    const height = 300;
    const padding = screenSize === 'mobile' ? 40 : 50;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const maxBalance = Math.max(...rothData.map((d: any) => d.balance));
    const minAge = rothData[0].age;
    const maxAge = rothData[rothData.length - 1].age;

    const xScale = (age: number) => padding + ((age - minAge) / (maxAge - minAge)) * chartWidth;
    const yScale = (balance: number) => height - padding - (balance / maxBalance) * chartHeight;

    const createPath = (data: any[], color: string) => {
      const pathData = data.map((d, i) => {
        const x = xScale(d.age);
        const y = yScale(d.balance);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      }).join(' ');
      return <path key={color} d={pathData} fill="none" stroke={color} strokeWidth="3" />;
    };

    const gridLines = [];
    for (let i = 0; i <= 4; i++) {
      const y = height - padding - (chartHeight / 4) * i;
      const value = (maxBalance / 4) * i;
      gridLines.push(
        <g key={`grid-${i}`}>
          <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#e5e7eb" strokeWidth="1" />
          <text x={padding - 10} y={y} textAnchor="end" fontSize={screenSize === 'mobile' ? '10' : '12'} fill="#64748b" dominantBaseline="middle">
            ${(value / 1000).toFixed(0)}K
          </text>
        </g>
      );
    }

    const ageLabels = [];
    const ageStep = Math.ceil((maxAge - minAge) / 5);
    for (let age = minAge; age <= maxAge; age += ageStep) {
      const x = xScale(age);
      ageLabels.push(
        <text key={`age-${age}`} x={x} y={height - padding + 20} textAnchor="middle" fontSize={screenSize === 'mobile' ? '10' : '12'} fill="#64748b">
          {age}
        </text>
      );
    }

    return (
      <svg width={width} height={height} style={{ maxWidth: '100%', height: 'auto' }}>
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#cbd5e1" strokeWidth="2" />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#cbd5e1" strokeWidth="2" />
        {gridLines}
        {ageLabels}
        {createPath(principalData, '#dc2626')}
        {createPath(taxableData, '#10b981')}
        {createPath(rothData, '#2563eb')}
        <text x={width / 2} y={20} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#1f2937">
          {t('chartTitle')}
        </text>
        <text x={width / 2} y={height - 10} textAnchor="middle" fontSize="12" fill="#64748b">
          {t('chartXAxis')}
        </text>
      </svg>
    );
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>{t('toolTitle')}</h2>
          </div>

          <div style={styles.calculatorContent}>
            <div>
              <form style={styles.calculatorForm} onSubmit={handleCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="current-balance">{t('currentBalanceLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="current-balance"
                    value={inputs.currentBalance}
                    onChange={(e) => handleInputChange('currentBalance', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="100"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="annual-contribution">{t('annualContributionLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="annual-contribution"
                    value={inputs.annualContribution}
                    onChange={(e) => handleInputChange('annualContribution', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="100"
                    disabled={inputs.maximize}
                    required
                  />
                  <span style={styles.inputHint}>{t('contributionHint')}</span>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('maximizeLabel')}</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="maximize"
                        checked={!inputs.maximize}
                        onChange={() => handleMaximizeChange(false)}
                      />
                      {t('maximizeNo')}
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="maximize"
                        checked={inputs.maximize}
                        onChange={() => handleMaximizeChange(true)}
                      />
                      {t('maximizeYes')}
                    </label>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="return-rate">{t('returnRateLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="return-rate"
                    value={inputs.returnRate}
                    onChange={(e) => handleInputChange('returnRate', parseFloat(e.target.value) || 0)}
                    min="0"
                    max="20"
                    step="0.1"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="current-age">{t('currentAgeLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="current-age"
                    value={inputs.currentAge}
                    onChange={(e) => {
                      const age = parseInt(e.target.value) || 30;
                      handleInputChange('currentAge', age);
                      if (inputs.maximize) {
                        handleInputChange('annualContribution', age >= 50 ? 8000 : 7000);
                      }
                    }}
                    min="18"
                    max="100"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="retirement-age">{t('retirementAgeLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="retirement-age"
                    value={inputs.retirementAge}
                    onChange={(e) => handleInputChange('retirementAge', parseInt(e.target.value) || 65)}
                    min="18"
                    max="100"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="tax-rate">{t('taxRateLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="tax-rate"
                    value={inputs.taxRate}
                    onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value) || 0)}
                    min="0"
                    max="50"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputHint}>{t('taxRateHint')}</span>
                </div>

                <button type="submit" style={styles.calculateButton}>
                  {t('calculateButton')}
                </button>

                {error && (
                  <div style={styles.errorMessage}>{error}</div>
                )}
              </form>
            </div>

            {results && (
              <div style={styles.results}>
                <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>

                <div style={styles.resultComparison}>
                  <table style={styles.comparisonTable}>
                    <thead>
                      <tr>
                        <th style={{ padding: '0.75rem', textAlign: 'left', background: '#f3f4f6', fontWeight: 700, color: '#1f2937', borderBottom: '1px solid #e5e7eb' }}></th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', background: '#f3f4f6', fontWeight: 700, color: '#1f2937', borderBottom: '1px solid #e5e7eb' }}>{t('rothIraColumn')}</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', background: '#f3f4f6', fontWeight: 700, color: '#1f2937', borderBottom: '1px solid #e5e7eb' }}>{t('taxableColumn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: '#fef3c7', fontWeight: 700, fontSize: '1.1rem' }}>
                        <td style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{t('balanceAtRetirement')}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{formatCurrency(results.rothBalance)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{formatCurrency(results.taxableBalance)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{t('totalPrincipal')}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{formatCurrency(results.totalPrincipal)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{formatCurrency(results.totalPrincipal)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{t('totalInterest')}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{formatCurrency(results.rothInterest)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{formatCurrency(results.taxableInterest)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{t('totalTax')}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>$0</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{formatCurrency(results.totalTax)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={styles.resultSummary}>
                  {results.difference > 0
                    ? t('summaryPositive', { difference: formatCurrency(results.difference), age: inputs.retirementAge })
                    : t('summaryNegative', { difference: formatCurrency(Math.abs(results.difference)), age: inputs.retirementAge })}
                </div>

                <div style={styles.chartContainer}>
                  <div style={styles.chartTitle}>{t('chartContainerTitle')}</div>
                  {drawChart()}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
          <li style={styles.listItem}>{t('howToUseStep5')}</li>
          <li style={styles.listItem}>{t('howToUseStep6')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight1Title')}</h3>
          <p style={styles.paragraph}>{t('insight1Content')}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight2Title')}</h3>
          <p style={styles.paragraph}>{t('insight2Content')}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight3Title')}</h3>
          <p style={styles.paragraph}>{t('insight3Content')}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight4Title')}</h3>
          <p style={styles.paragraph}>{t('insight4Content')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>

        <h3 style={styles.h3}>{t('detailsSubtitle1')}</h3>
        <p style={styles.paragraph}>{t('detailsContent1')}</p>

        <h3 style={styles.h3}>{t('detailsSubtitle2')}</h3>
        <p style={styles.paragraph}>{t('detailsContent2')}</p>

        <h3 style={styles.h3}>{t('detailsSubtitle3')}</h3>
        <p style={styles.paragraph}>{t('detailsContent3')}</p>

        <h3 style={styles.h3}>{t('detailsSubtitle4')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsList1Item1')}</li>
          <li style={styles.listItem}>{t('detailsList1Item2')}</li>
          <li style={styles.listItem}>{t('detailsList1Item3')}</li>
          <li style={styles.listItem}>{t('detailsList1Item4')}</li>
        </ul>

        <h3 style={styles.h3}>{t('detailsSubtitle5')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsList2Item1')}</li>
          <li style={styles.listItem}>{t('detailsList2Item2')}</li>
          <li style={styles.listItem}>{t('detailsList2Item3')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.nerdwallet.com/investing/calculators/roth-ira-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.sofi.com/calculators/ira-contribution-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.bankrate.com/retirement/roth-ira-plan-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.calculator.net/ira-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://investor.vanguard.com/investor-resources-education/iras/roth-ira-income-limits"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference5')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.thrivent.com/insights/tools/roth-ira-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference6')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.schwab.com/ira/ira-calculators/roth-vs-traditional"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference7')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}